import { colorBands } from "src/organization/pages/project/environment/metrics/constant";

import { HttpMetricsType, MetricsInfoName } from "../types";

import { TimeRangeImpactType } from "./table/type";

export const findMetricsValue = (
  data?: HttpMetricsType,
  name?: MetricsInfoName
) => data?.values.find(v => v.info.name === name)?.value;

export const sortMetricsByImpact = (a: HttpMetricsType, b: HttpMetricsType) => {
  const sumA = findMetricsValue(a, "request.url.method.impact")?.sum || 0;
  const sumB = findMetricsValue(b, "request.url.method.impact")?.sum || 0;
  return sumB - sumA;
};

export const getTopRangeImpact = (
  metrics: HttpMetricsType[],
  data: HttpMetricsType[],
  topN: number
) => {
  // Use a Map for grouping
  const urlMethodGroups = new Map<string, HttpMetricsType[]>();

  //TODO Precompute the total request count from data
  // slow move to initial data processing
  const requestCountSum = data.reduce(
    (sum, mtx) => sum + (findMetricsValue(mtx, "request.count")?.sum || 0),
    0
  );

  metrics.forEach(metric => {
    const url = metric?.["http.request.url"] || "";
    const method = metric?.["http.request.method"] || "";
    const urlMethod = url + method;

    if (!urlMethodGroups.has(urlMethod)) {
      urlMethodGroups.set(urlMethod, []);
    }
    urlMethodGroups.get(urlMethod)?.push(metric);
  });

  // Calculate impacts and store them
  const impacts: TimeRangeImpactType[] = [];
  urlMethodGroups.forEach(urlMethodMetrics => {
    const { totalAvgSum, totalAvgCount, p95DurationSum, p95DurationCount } =
      urlMethodMetrics.reduce(
        (sum, mtx) => {
          const durationAvg = findMetricsValue(
            mtx,
            "request.url.method.duration.avg"
          );
          const durationP95 = findMetricsValue(
            mtx,
            "request.url.method.duration.p95"
          );
          sum.totalAvgSum += durationAvg?.sum || 0;
          sum.totalAvgCount += durationAvg?.count || 0;

          sum.p95DurationSum += durationP95?.sum || 0;
          sum.p95DurationCount += durationP95?.count || 0;
          return sum;
        },
        {
          totalAvgSum: 0,
          p95DurationSum: 0,
          totalAvgCount: 0,
          p95DurationCount: 0
        }
      );

    const urlMethodSum = urlMethodMetrics.reduce(
      (sum, mtx) =>
        sum + (findMetricsValue(mtx, "request.url.method.count")?.sum || 0),
      0
    );

    const impact = (urlMethodSum / requestCountSum) * 100;
    const metrics = urlMethodMetrics[0];
    const method = metrics["http.request.method"] || "";
    const url = metrics["http.request.url"] || "";

    const avgDurationSeconds =
      totalAvgCount > 0 ? totalAvgSum / totalAvgCount : 0;
    const p95DurationSeconds =
      p95DurationCount > 0 ? p95DurationSum / p95DurationCount : 0;

    impacts.push({
      method,
      url,
      count: urlMethodSum,
      avgDuration: avgDurationSeconds * 1000,
      p95Duration: p95DurationSeconds * 1000,
      impact,
      metrics
    });
  });
  return impacts
    .sort((a, b) => b.impact - a.impact)
    .slice(0, topN)
    .map((v, i) => ({ ...v, color: colorBands[i] }));
};

/**
 * Get the top impact series for the overall time range selected
 */
export const getTimeRangeImpactSeries = (
  data: HttpMetricsType[],
  rangeImpact: TimeRangeImpactType[]
) => {
  const timestampMap = new Map<string, HttpMetricsType[]>();

  //sort all the data by timestamp
  data.forEach(d => {
    const timestamp = d.timestamp;
    if (!timestampMap.has(timestamp)) {
      timestampMap.set(timestamp, []);
    }
    timestampMap.get(timestamp)?.push(d);
  });

  const impactSeries: HttpMetricsType[][] = [];
  timestampMap.forEach((items, timestamp) => {
    // for each timestamp sort by impact and get the top N items
    // not all timestamp will have upto N items leaving some items with less than N impact data
    const timestampItems = items.filter(item => item["http.request.url"]);

    rangeImpact.forEach((impact, impactIndex) => {
      let item = timestampItems.find(
        item =>
          item["http.request.method"] === impact.method &&
          item["http.request.url"] === impact.url
      );

      //create series if it does not exist
      impactSeries[impactIndex] = impactSeries[impactIndex] || [];
      if (!item) {
        item = {
          ...impact.metrics,
          date: new Date(timestamp),
          timestamp,
          dimension: impact.metrics.dimension,
          values: []
        };
      }
      impactSeries[impactIndex].push(item);
    });
  });
  return impactSeries;
};
